import { applyBonusBps, type Fixed, fixedAdd, fixedFromInteger } from '@kenocyber/shared';

/**
 * Base attributes vs final attributes.
 *
 * A base attribute is a starting value plus the level-up points invested in it.
 * Equipment contributes percentage bonuses on top; the two are stored
 * separately and combined only at the edge, so the Overview screen can show
 * base / bonus / final columns honestly.
 *
 * "Damage per shot" is always the damage of a SINGLE projectile. A multi-
 * projectile cannon applies each hit individually.
 */

export const LEVEL_UP_ATTRIBUTES = ['baseDamage', 'baseAttackSpeed', 'baseHealth'] as const;
export type LevelUpAttribute = (typeof LEVEL_UP_ATTRIBUTES)[number];

/** One point per player level, spent on exactly one of the three attributes. */
export const POINTS_PER_LEVEL = 1;

/**
 * Starting base damage: **1**. Decided.
 *
 * A brand-new account fires one projectile for 1 damage before any bonus.
 */
export const INITIAL_BASE_DAMAGE_UNITS = 1;

/**
 * Starting base health: **PENDING**. Not decided, not guessed.
 * @see docs/OPEN_QUESTIONS.md
 */
export const INITIAL_BASE_HEALTH_UNITS_PENDING = null;

/**
 * Starting base attack speed and its unit: **PENDING**.
 * Whether it is expressed in volleys per second, per minute or as a cooldown
 * has not been decided either.
 * @see docs/OPEN_QUESTIONS.md
 */
export const INITIAL_BASE_ATTACK_SPEED_UNITS_PENDING = null;

export interface BaseAttributes {
  /** Points allocated to base damage. */
  readonly baseDamagePoints: number;
  /** Points allocated to base attack speed. */
  readonly baseAttackSpeedPoints: number;
  /** Points allocated to base health. */
  readonly baseHealthPoints: number;
}

/** A fresh account: no points spent anywhere. */
export const INITIAL_BASE_ATTRIBUTES: BaseAttributes = Object.freeze({
  baseDamagePoints: 0,
  baseAttackSpeedPoints: 0,
  baseHealthPoints: 0,
});

/**
 * Percentage bonuses, additive in basis points within this container.
 *
 * Whether different bonus SOURCES stack additively or multiplicatively with
 * each other is a pending balance decision (`docs/OPEN_QUESTIONS.md`); this
 * container only aggregates what it is given.
 */
export interface PercentBonuses {
  readonly damageBps: number;
  readonly attackSpeedBps: number;
  readonly healthBps: number;
}

/**
 * The starting bonus set: **0%**, i.e. a final multiplier of exactly 1x.
 * Decided.
 */
export const NO_BONUSES: PercentBonuses = Object.freeze({
  damageBps: 0,
  attackSpeedBps: 0,
  healthBps: 0,
});

export const INITIAL_PERCENT_BONUSES = NO_BONUSES;

/**
 * How many attribute units one allocated point is worth.
 *
 * CONFIGURABLE PLACEHOLDER - the real conversion curve is a balance decision
 * that has not been made. Keeping it a named constant (instead of scattering
 * `1`s) means the future change is one line.
 */
export const UNITS_PER_POINT_PLACEHOLDER = 1;

export interface FinalAttributes {
  /** Damage of a single projectile. */
  readonly damagePerShot: Fixed;
  readonly attackSpeed: Fixed;
  readonly health: Fixed;
}

/**
 * Pure combination step: starting base + allocated points -> units, then
 * percentage bonuses applied once.
 *
 * Only `damagePerShot` rests on decided numbers today: base 1, 0% bonus, so a
 * fresh account computes exactly 1. Health and attack speed still lack a
 * decided starting base, so their outputs are provisional until
 * `docs/OPEN_QUESTIONS.md` is resolved.
 */
export function computeFinalAttributes(
  base: BaseAttributes,
  bonuses: PercentBonuses,
): FinalAttributes {
  const damageUnits = fixedAdd(
    fixedFromInteger(INITIAL_BASE_DAMAGE_UNITS),
    fixedFromInteger(base.baseDamagePoints * UNITS_PER_POINT_PLACEHOLDER),
  );
  const speedUnits = fixedFromInteger(base.baseAttackSpeedPoints * UNITS_PER_POINT_PLACEHOLDER);
  const healthUnits = fixedFromInteger(base.baseHealthPoints * UNITS_PER_POINT_PLACEHOLDER);
  return {
    damagePerShot: applyBonusBps(damageUnits, bonuses.damageBps),
    attackSpeed: applyBonusBps(speedUnits, bonuses.attackSpeedBps),
    health: applyBonusBps(healthUnits, bonuses.healthBps),
  };
}
